<template>
  <section class="app-ecommerce-details">
    <b-overlay :show="isLoading" rounded="sm">

      <b-alert variant="danger" :show="!product">
        <h4 class="alert-heading">Produto não encontrado</h4>
        <div class="alert-body">
          Nenhum item encontrado.
          <b-link class="alert-link" :to="{ name: 'apps-e-commerce-shop' }">Ver catálogo</b-link>
        </div>
      </b-alert>

      <!-- Totais (fora do modal) -->
      <b-row class="pb-2 d-flex justify-content-end mr-1" v-if="!isModal && items.length > 0">
        <b-col cols="auto" class="text-right align-self-end">
          <p class="mb-0"><strong>Total de unidades:</strong> {{ totalUnidades }}</p>
          <p class="mb-0"><strong>Total:</strong>
            R$ {{ (totalValorFinal || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
          </p>
        </b-col>
      </b-row>

      <b-row class="my-2" v-if="product">

        <!-- Miniaturas laterais -->
        <b-col :cols="isModal ? 2 : 1" class="imagens-container pr-0 mt-1">
          <div class="imagens-item">
            <div
              v-for="(img, index) in (product.imagens || []).slice(0, 5)"
              :key="index"
            >
              <div
                class="rounded imagens-item-img"
                :class="{ 'selected-img': product.item_img_princial === img.caminho }"
                :style="isModal ? { width: '40px', height: '40px' } : {}"
              >
                <b-img
                  :src="img.caminho ? img.caminho : imagemPadrao"
                  :alt="`${product.item_name}-${product.item_id}`"
                  class="img-fluid"
                  @click="product.item_img_princial = img.caminho"
                  @mouseover="product.item_img_princial = img.caminho"
                />
              </div>
            </div>
            <div
              v-if="(product.imagens || []).length > 5"
              class="d-flex justify-content-center align-items-center bg-grey-1 rounded more-imgs"
              @click="$bvModal.show('modal-gallery-imagens')"
            >
              +{{ (product.imagens || []).length - 5 }}
            </div>
          </div>
        </b-col>

        <!-- Imagem principal -->
        <b-col cols="12" sm="12" :md="isModal ? 4 : 4" class="px-auto mt-1">
          <div
            class="main-image-wrapper"
            :class="{ 'is-zooming': isZooming }"
            @click="$bvModal.show('modal-gallery-imagens')"
            @mouseenter="onZoomEnter"
            @mousemove="onZoomMove"
            @mouseleave="onZoomLeave"
          >
            <b-img
              ref="mainImage"
              :src="imagemPrincipalProduto"
              :alt="`${product.item_name}-${product.item_id}`"
              :style="mainImageStyle"
              class="img-fluid product-main-img"
            />
            <div class="image-zoom-hint">
              <feather-icon
                :icon="isTouchDevice ? 'Maximize2Icon' : 'ZoomInIcon'"
                size="14"
                class="mr-25"
              />
              {{ isTouchDevice ? 'Toque para ampliar' : 'Passe o mouse para dar zoom' }}
            </div>
          </div>
        </b-col>

        <!-- Painel de informações -->
        <b-col cols="12" :md="isModal ? 6 : 7">

          <!-- Código do item (item_id = código TOTVS/ISBN) / Marca -->
          <div class="d-flex align-items-center mb-50">
            <b-badge variant="light-primary" class="mr-75 ref-badge">
              {{ product.item_id || product.item_codigo_origem || product.referencia }}
            </b-badge>
            <span class="text-muted font-small-3">{{ product.item_marca }}</span>
          </div>

          <!-- Nome do produto -->
          <h4 :class="['product-title', isModal ? 'font-medium-2' : 'font-medium-4']">
            {{ product.item_name }}
          </h4>

          <!-- Preço -->
          <div class="price-block my-1">
            <span class="price-label text-muted font-small-2">
              {{ valorDeCustoVisual ? 'Valor de custo' : 'Valor' }}
            </span>
            <div v-if="isModal && requireCalculatedPrice && (!calculatedPriceReady || calculatedPriceLoading) && !(impostoPreview && !calculatedPriceLoading && precoExibicao === 0)" class="text-muted">
              {{ calculatedPriceLoading ? 'Calculando preço…' : 'Preço indisponível' }}
            </div>
            <div v-else class="d-flex align-items-baseline">
              <span class="price-currency text-muted mr-25">R$</span>
              <span class="price-value text-primary">
                {{ precoVisual === null ? '—' : precoVisual.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
              </span>
            </div>
          </div>

          <!-- Estoque -->
          <div v-if="!isClientePerfil && !ocultarEstoque" class="estoque-block mb-1">
            <span class="text-muted font-small-2 mr-50">Estoque:</span>
            <span v-if="estoqueLoading">
              <b-spinner small variant="secondary" />
            </span>
            <span v-else-if="estoqueInfo !== null">
              <estoque-badge :estoque-info="estoqueInfo" />
              <small
                v-if="estoqueInfo.dataprevista && estoqueInfo.quantidade === 0"
                class="text-muted ml-50 font-small-2"
              >
                Previsão: {{ estoqueInfo.dataprevista }}
              </small>
            </span>
            <b-badge v-else variant="secondary" style="font-size:0.72rem">
              <feather-icon icon="PackageIcon" size="10" class="mr-25" />
              Não consultado
            </b-badge>
          </div>

          <!-- Ficha técnica (idioma, coleção, componente, nível educacional, páginas, ano...) -->
          <item-ficha-tecnica :item-id="product.item_id" />

          <hr class="my-1">

          <!-- Quantidade -->
          <div class="qty-block">
            <h6 :class="['qty-label', isModal ? 'font-small-3' : '']">QUANTIDADE</h6>

            <div class="qty-control">
              <button
                class="qty-btn qty-btn-minus"
                @click="decrementQty"
                :disabled="formQty <= 1"
              >
                <feather-icon icon="MinusIcon" size="14" />
              </button>
              <input
                v-model.number="formQty"
                type="number"
                min="1"
                class="qty-input"
                @keydown.enter="adicionarItem"
              />
              <button
                class="qty-btn qty-btn-plus"
                @click="incrementQty"
              >
                <feather-icon icon="PlusIcon" size="14" />
              </button>
            </div>

            <!-- Alerta de estoque baixo (somente quando a trava de venda sem estoque está ativa) -->
            <small
              v-if="!isClientePerfil && !ocultarEstoque && $store.getters['preferencias/bloquearVendaSemEstoque'] && estoqueInfo && estoqueInfo.quantidade !== 'INF' && estoqueInfo.quantidade !== null && formQty > estoqueInfo.quantidade && estoqueInfo.quantidade > 0"
              class="text-warning d-block mt-25"
            >
              <feather-icon icon="AlertTriangleIcon" size="12" class="mr-25" />
              Quantidade maior que o estoque disponível<template v-if="!isClientePerfil && !(isRepresentantePerfil && Number(estoqueInfo.quantidade) > 50)"> ({{ estoqueInfo.quantidade }} un)</template>
            </small>
          </div>

          <!-- Botão Adicionar -->
          <b-button
            variant="primary"
            block
            class="add-btn mt-1"
            :disabled="formQty < 1 || (isModal && requireCalculatedPrice && (!calculatedPriceReady || calculatedPriceLoading))"
            @click="adicionarItem"
          >
            <feather-icon icon="ShoppingCartIcon" size="16" class="mr-50" />
            <template v-if="isModal && requireCalculatedPrice && calculatedPriceLoading">Calculando preço…</template>
            <template v-else>Adicionar ao Pedido</template>
          </b-button>
          <div
            v-if="isModal && requireCalculatedPrice && !calculatedPriceReady && !calculatedPriceLoading"
            class="text-warning font-small-2 mt-50"
          >
            <feather-icon icon="AlertTriangleIcon" size="12" class="mr-25" />
            {{ calculatedPriceMessage || 'Calcule o preço atual antes de adicionar.' }}
            <b-link class="ml-25" @click="$emit('retry-calculated-price')">Tentar novamente</b-link>
          </div>

          <!-- Itens adicionados -->
          <div v-if="!isModal && items.length > 0" class="mt-2">
            <div class="d-flex align-items-center mb-75">
              <feather-icon icon="CheckCircleIcon" size="16" class="text-success mr-50" />
              <h6 class="mb-0">Itens adicionados</h6>
            </div>
            <div class="added-items-list">
              <div
                v-for="(item, index) in items"
                :key="index"
                class="added-item-row d-flex align-items-center justify-content-between"
              >
                <div class="d-flex align-items-center">
                  <b-badge variant="light-primary" class="qty-badge mr-50">
                    {{ item.valor }}x
                  </b-badge>
                  <span class="font-small-3">{{ item.item_name || product.item_name }}</span>
                </div>
                <div class="d-flex align-items-center">
                  <span class="text-muted font-small-3 mr-75">
                    {{ subtotalVisual(item) }}
                  </span>
                  <button type="button" class="remove-btn" @click="removeItem(index, item)">
                    <feather-icon icon="XIcon" size="14" />
                  </button>
                </div>
              </div>
            </div>
          </div>

        </b-col>
      </b-row>
    </b-overlay>

    <!-- Modal galeria -->
    <b-modal
      id="modal-gallery-imagens"
      hide-footer
      hide-header
      body-bg-variant="transparent"
      @shown="initSwiper"
    >
      <swiper
        ref="refSwiperGallery"
        class="swiper-gallery gallery-top"
        :options="swiperOptions"
        :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
      >
        <swiper-slide v-for="(data, index) in (product.imagens || [])" :key="index">
          <b-img :src="data.caminho" fluid />
        </swiper-slide>
        <div slot="button-next" class="swiper-button-next swiper-button-white" />
        <div slot="button-prev" class="swiper-button-prev swiper-button-white" />
      </swiper>
    </b-modal>
  </section>
</template>

<script>
import {
  BRow, BCol, BImg, BLink, BButton, BAlert, BBadge, BOverlay, BSpinner,
} from 'bootstrap-vue';
import Ripple from 'vue-ripple-directive';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import { mapGetters } from 'vuex';
import { Swiper, SwiperSlide } from 'vue-awesome-swiper';
import 'swiper/css/swiper.css';
import { IMAGEM_PADRAO, imagemProduto } from '@/utils/imagemUrl';
import EstoqueBadge from '@/components/EstoqueBadge.vue';
import { ocultarEstoqueCertificacao, tipoPedidoSelecionadoParaEstoque } from '@/utils/estoqueVisibilidade';
import { precoComDescontoConfirmado, precoFinalSimulado } from '@/utils/precoProveniencia';
import { precoVisualizacao, ehValorDeCusto } from '@/utils/precoVisualizacao';
import { isPerfilClienteAtivo } from '@/libs/perfilCliente';
import { isPerfilRepresentanteAtivo } from '@/libs/perfilCliente';
import ItemFichaTecnica from '@/components/ItemFichaTecnica.vue';
import SLSEstoqueService from '@/services/SistemaDKP/SLSEstoque.service';

export default {
  name: 'ECommerceProductDetails',
  directives: { Ripple },
  components: {
    BRow, BCol, BImg, BLink, BButton, BAlert, BBadge, BOverlay, BSpinner,
    Swiper, SwiperSlide, EstoqueBadge, ItemFichaTecnica,
  },
  props: {
    product: {
      type: Object,
      required: true,
      default: () => ({
        item_id: null,
        item_name: '',
        referencia: '',
        item_marca: '',
        item_price: 0,
        item_price_exibicao: 0,
        item_img_princial: '',
        imagens: [],
      }),
    },
    json: {
      type: Array,
      required: false,
      default: () => [],
    },
    isModal: {
      type: Boolean,
      default: false,
    },
    impostoPreview: {
      type: Object,
      required: false,
      default: null,
    },
    requireCalculatedPrice: {
      type: Boolean,
      default: false,
    },
    calculatedPriceReady: {
      type: Boolean,
      default: true,
    },
    calculatedEffectivePrice: {
      type: Number,
      default: null,
    },
    calculatedPriceLoading: {
      type: Boolean,
      default: false,
    },
    calculatedPriceMessage: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      imagemPadrao: IMAGEM_PADRAO,
      formQty: 1,
      items: [],
      isLoading: false,
      estoqueInfo: null,
      estoqueLoading: false,
      isZooming: false,
      zoomOriginX: 50,
      zoomOriginY: 50,
      zoomScale: 2.5,
      isTouchDevice: false,
      swiperOptions: {
        loop: true,
        loopedSlides: 5,
        spaceBetween: 10,
        navigation: {
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
        },
      },
    };
  },
  computed: {
    tipoPedidoVisual() {
      const form = this.$store.getters['InfoPedido/GET_FORM_INFO_PEDIDO'] || {};
      return tipoPedidoSelecionadoParaEstoque(this.$store)
        || form.dataCapaPedido?.tipopedidoid || this.impostoPreview?._tipoPedido;
    },
    valorDeCustoVisual() {
      return ehValorDeCusto(this.tipoPedidoVisual, this.pedidoDoacao || this.impostoPreview?._pedidoDoacao === true);
    },
    precoVisual() {
      if (this.calculatedPriceLoading) return null;
      return precoVisualizacao({
        item: this.product,
        resposta: this.impostoPreview,
        tipoPedido: this.tipoPedidoVisual,
        doacao: this.pedidoDoacao,
      });
    },
    ocultarEstoque() {
      return ocultarEstoqueCertificacao(tipoPedidoSelecionadoParaEstoque(this.$store));
    },
    imagemPrincipalProduto() {
      return imagemProduto(this.product);
    },
    // Perfil Cliente não vê números de estoque, só disponibilidade
    isClientePerfil() {
      return isPerfilClienteAtivo();
    },
    isRepresentantePerfil() {
      return isPerfilRepresentanteAtivo();
    },
    ...mapGetters('InfoPedido', [
      'GET_CLIENTE_DATA',
      'GET_ENDERECO_ENTREGA',
      'GET_DATA_CAPA_PEDIDO',
    ]),
    precoExibicao() {
      if (this.isModal && this.requireCalculatedPrice) {
        return this.calculatedPriceReady && !this.calculatedPriceLoading
          ? this.calculatedEffectivePrice : null;
      }
      const p = this.product;
      if (!p) return 0;
      // The fiscal selection depends on order type and this SKU's assessment flag.
      if (this.impostoPreview) {
        const form = this.$store.getters['InfoPedido/GET_FORM_INFO_PEDIDO'] || {};
        const calculated = precoFinalSimulado({
          ...this.impostoPreview,
          _pedidoDoacao: this.pedidoDoacao,
          _tipoPedido: form.tipoPedidoCliente?.tipopedidoid || form.dataCapaPedido?.tipopedidoid,
        });
        return Number.isFinite(calculated) ? calculated : 0;
      }
      const val = p.item_price_exibicao ?? p.item_price ?? 0;
      return parseFloat(val) || 0;
    },
    pedidoDoacao() {
      const form = this.$store.getters['InfoPedido/GET_FORM_INFO_PEDIDO'] || {};
      const tipo = form.tipoPedidoCliente?.tipopedidoid || form.dataCapaPedido?.tipopedidoid || form.tipoPedido?.tipopedidoid;
      return String(tipo || '').toUpperCase() === 'WD';
    },
    temDescontoFiscal() {
      if (!this.impostoPreview) return false;
      const form = this.$store.getters['InfoPedido/GET_FORM_INFO_PEDIDO'] || {};
      return precoComDescontoConfirmado({
        ...this.impostoPreview,
        _pedidoDoacao: this.pedidoDoacao,
        _tipoPedido: form.tipoPedidoCliente?.tipopedidoid || form.dataCapaPedido?.tipopedidoid,
      });
    },
    // Preço de tabela (referência quando há desconto).
    precoBrutoComImposto() {
      const v = this.impostoPreview && this.impostoPreview.precoComImposto;
      return v !== undefined && v !== null && Number(v) > 0 ? parseFloat(v) || 0 : 0;
    },
    precoBase() {
      const p = this.product;
      if (!p) return 0;
      const val = p.item_price ?? 0;
      return parseFloat(val) || 0;
    },
    temImpostoAplicado() {
      return this.impostoPreview
        && this.impostoPreview.precoComImposto !== undefined
        && this.impostoPreview.precoComImposto !== null
        && Number(this.impostoPreview.precoComImposto) > 0
        && Math.abs(this.precoExibicao - this.precoBase) > 0.001;
    },
    totalUnidades() {
      return this.items.reduce((sum, item) => sum + item.valor, 0);
    },
    totalValorFinal() {
      return this.items.reduce((sum, item) => sum + (parseFloat(item.precoUnitario || 0) * item.valor), 0);
    },
    mainImageStyle() {
      const base = this.isModal
        ? { maxHeight: '300px', width: 'auto', margin: '0 auto', display: 'block' }
        : { minHeight: '350px', padding: '0' };
      if (this.isZooming && !this.isTouchDevice) {
        return {
          ...base,
          transformOrigin: `${this.zoomOriginX}% ${this.zoomOriginY}%`,
          transform: `scale(${this.zoomScale})`,
          cursor: 'zoom-in',
        };
      }
      return base;
    },
  },
  watch: {
    'product.item_id': {
      immediate: false,
      handler(newId) {
        if (newId) this.carregarEstoque();
      },
    },
  },
  created() {
    const formData = this.$store.getters['InfoPedido/GET_VALIDATION_FORM_INFO_PEDIDO'];
    if (!formData) {
      this.$bvModal.hide('modal-top-selected-embalagens');
      this.$bvModal.show('modal-center-info-pedido');
    }
  },
  mounted() {
    this.carregarEstoque();
    this.$root.$on('info-pedido:updated', this.onInfoPedidoUpdated);
    // Detecta dispositivo SEM capacidade de hover (mobile real) — desativa zoom-on-hover.
    // Usamos apenas (hover: none) porque (pointer: coarse) gera falso-positivo
    // em iframes/preview de browsers desktop.
    this.isTouchDevice = window.matchMedia('(hover: none)').matches;
  },
  beforeDestroy() {
    this.$root.$off('info-pedido:updated', this.onInfoPedidoUpdated);
  },
  methods: {
    subtotalVisual(item) {
      const preco = precoVisualizacao({ item, tipoPedido: this.tipoPedidoVisual, doacao: this.pedidoDoacao });
      return preco === null ? '—' : (preco * item.valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    },
    precoComDescontoConfirmado,
    onZoomEnter() {
      if (this.isTouchDevice) return;
      this.isZooming = true;
    },
    onZoomMove(event) {
      if (this.isTouchDevice || !this.isZooming) return;
      const rect = event.currentTarget.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * 100;
      const y = ((event.clientY - rect.top) / rect.height) * 100;
      this.zoomOriginX = Math.max(0, Math.min(100, x));
      this.zoomOriginY = Math.max(0, Math.min(100, y));
    },
    onZoomLeave() {
      this.isZooming = false;
    },
    async carregarEstoque() {
      if (!this.product?.item_id) return;
      this.estoqueLoading = true;
      try {
        const service = new SLSEstoqueService();
        // Detalhe do produto: sempre valor FRESCO do TOTVS (bypassa o cache de
        // sessão) — evita divergência com o preview do modal (ex.: 0 × 90 un).
        const result = await service.consultarEstoque([this.product.item_id], { fresh: true });
        this.estoqueInfo = result[this.product.item_id] || null;
      } catch (e) {
        console.error('[ECommerceProductDetails] Erro ao carregar estoque:', e);
      } finally {
        this.estoqueLoading = false;
      }
    },

    incrementQty() {
      this.formQty = (parseInt(this.formQty) || 0) + 1;
    },

    decrementQty() {
      const v = (parseInt(this.formQty) || 2) - 1;
      this.formQty = v < 1 ? 1 : v;
    },

    adicionarItem() {
      if (this.isModal && this.requireCalculatedPrice
        && (!this.calculatedPriceReady || this.calculatedPriceLoading)) {
        this.$emit('retry-calculated-price');
        return;
      }
      const qty = parseInt(this.formQty);
      if (!qty || qty < 1) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Atenção',
            text: 'Informe uma quantidade válida.',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        });
        return;
      }

      const hasDuplicate = this.items.some((i) => i.item_id === this.product.item_id);
      if (hasDuplicate) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Item já adicionado',
            text: 'Para alterar a quantidade, remova o item e adicione novamente.',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        });
        return;
      }

      const newItem = {
        item_id: this.product.item_id,
        item_name: this.product.item_name,
        referencia: this.product.referencia,
        valor: qty,
        // In modal flows the parent must provide the successful, current-context
        // calculated value. Never persist the display/base fallback.
        precoUnitario: this.isModal && this.requireCalculatedPrice
          ? this.calculatedEffectivePrice
          : this.precoExibicao,
        precoBruto: this.precoBrutoComImposto,
      };

      this.items.push(newItem);
      this.$emit('update:grade', this.items);
      this.formQty = 1;

      if (this.isModal) {
        this.$emit('confirmar');
      } else {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Item adicionado',
            text: `${qty}x ${this.product.item_name}`,
            icon: 'ShoppingCartIcon',
            variant: 'success',
          },
        });
      }
    },

    removeItem(index, item) {
      this.items.splice(index, 1);
      this.$emit('remove-item', item);
      this.$emit('update:grade', this.items);
    },

    onInfoPedidoUpdated() {
      // reservado para recálculos futuros
    },

    initSwiper() {
      this.$nextTick(() => {
        if (this.$refs.refSwiperGallery?.$swiper) {
          this.$refs.refSwiperGallery.$swiper.update();
          this.$refs.refSwiperGallery.$swiper.navigation.update();
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@core/scss/base/pages/app-ecommerce-details.scss";

/* ── Imagens laterais ── */
.imagens-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
  cursor: pointer;
}

.imagens-item-img {
  border: 2px solid transparent;
  width: 52px;
  height: 52px;
  overflow: hidden;
  transition: border-color 0.2s;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &.selected-img,
  &:hover {
    border-color: var(--primary);
  }
}

.more-imgs {
  border: 1px solid var(--primary);
  width: 52px;
  height: 52px;
  font-size: 0.8rem;
  color: var(--primary);
  cursor: pointer;
}

/* ── Imagem principal ── */
.main-image-wrapper {
  position: relative;
  cursor: zoom-in;
  overflow: hidden;
  border-radius: 4px;

  &:hover .image-zoom-hint {
    opacity: 1;
  }
}

.product-main-img {
  width: 100%;
  object-fit: contain;
  transition: transform 0.15s ease-out;
  will-change: transform;
}

/* Em telas sem hover (mobile/touch), o zoom-on-hover é desativado e o
   tap segue abrindo a galeria fullscreen — comportamento já existente. */
@media (hover: none) {
  .main-image-wrapper {
    cursor: pointer;
  }
  .product-main-img {
    transform: none !important;
  }
  .image-zoom-hint {
    opacity: 0.85 !important;
  }
}

.image-zoom-hint {
  position: absolute;
  bottom: 8px;
  right: 8px;
  background: rgba(0,0,0,0.55);
  color: #fff;
  font-size: 0.72rem;
  padding: 4px 8px;
  border-radius: 4px;
  opacity: 0;
  transition: opacity 0.2s;
  pointer-events: none;
  display: flex;
  align-items: center;
}

/* Esconde a "etiqueta" enquanto o zoom desktop está ativo (não polui a visão) */
.main-image-wrapper.is-zooming .image-zoom-hint {
  opacity: 0;
}

/* ── Painel direito ── */
.ref-badge {
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.03em;
}

.product-title {
  font-weight: 700;
  line-height: 1.35;
  color: inherit;
}

.price-block {
  .price-label {
    display: block;
    line-height: 1.2;
    margin-bottom: 2px;
  }
  .price-currency {
    font-size: 1rem;
    font-weight: 500;
  }
  .price-value {
    font-size: 2rem;
    font-weight: 700;
    line-height: 1;
  }
}

.estoque-block {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
}

/* ── Controle de quantidade ── */
.qty-block {
  .qty-label {
    font-weight: 600;
    letter-spacing: 0.06em;
    margin-bottom: 8px;
    color: inherit;
  }
}

.qty-control {
  display: inline-flex;
  align-items: center;
  border: 1px solid #d8d6de;
  border-radius: 8px;
  overflow: hidden;
}

.qty-btn {
  background: transparent;
  border: none;
  padding: 8px 14px;
  cursor: pointer;
  color: var(--primary);
  display: flex;
  align-items: center;
  transition: background 0.15s;

  &:hover:not(:disabled) {
    background: rgba(var(--primary-rgb, 115, 103, 240), 0.08);
  }

  &:disabled {
    color: #b8b8b8;
    cursor: not-allowed;
  }
}

.qty-input {
  width: 64px;
  border: none;
  border-left: 1px solid #d8d6de;
  border-right: 1px solid #d8d6de;
  text-align: center;
  font-size: 1rem;
  font-weight: 600;
  padding: 6px 0;
  outline: none;
  appearance: textfield;
  -moz-appearance: textfield;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
}

/* ── Botão adicionar ── */
.add-btn {
  border-radius: 8px;
  padding: 10px;
  font-weight: 600;
  letter-spacing: 0.02em;
  transition: transform 0.1s, box-shadow 0.15s;

  &:not(:disabled):active {
    transform: scale(0.98);
  }
}

/* ── Lista itens adicionados ── */
.added-items-list {
  border: 1px solid #ebe9f1;
  border-radius: 8px;
  overflow: hidden;
}

.added-item-row {
  padding: 10px 12px;
  border-bottom: 1px solid #f3f2f7;
  transition: background 0.15s;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #fafafa;
  }
}

.qty-badge {
  font-size: 0.75rem;
  font-weight: 700;
  min-width: 36px;
  text-align: center;
}

.remove-btn {
  background: none;
  border: none;
  color: #b0b0b0;
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 2px;
  border-radius: 4px;
  transition: color 0.15s, background 0.15s;

  &:hover {
    color: #ea5455;
    background: rgba(234, 84, 85, 0.08);
  }
}

/* ── Responsivo ── */
@media (max-width: 768px) {
  .imagens-container {
    width: 100%;
    padding-bottom: 10px;
  }

  .imagens-item {
    flex-direction: row;
  }
}

/* ── Swiper galeria ── */
.swiper-container {
  width: 100%;
  height: 100%;
}

.swiper-slide img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
</style>
